// ═══════════════════════════════════════════════════════════════════════════
// Shared structural paragraph ID cache
// Sources: 'figureFootnote' (Run 9) and 'tableFootnote' (Run 12)
// Consumer: Run 6 (body text) — skips claimed IDs instead of re-detecting
//
// IMPORTANT: activeSources.add(source) always fires regardless of whether
// any IDs were found — so Run 6 knows a source RAN even if it found nothing.
// ═══════════════════════════════════════════════════════════════════════════

export type StructuralSource = 'figureFootnote' | 'tableFootnote';

// paraId → which source(s) claimed it
const claimedIds    = new Map<string, Set<StructuralSource>>();
// which sources have registered (run), even if they found 0 paras
const activeSources = new Set<StructuralSource>();

// ── Write API (called by Run 9 and Run 12) ────────────────────────────────

export function claimParaIds(ids: string[], source: StructuralSource): void {
  // Always mark source as active — even when ids is empty.
  // This tells Run 6 the source RAN and found nothing, so Run 6
  // knows not to fall through to footnote chain detection for that source.
  activeSources.add(source);

  if (ids.length === 0) {
    console.log(`[structuralParaCache] "${source}" ran but found 0 IDs. Marked active.`);
    return;
  }

  for (const id of ids) {
    const existing = claimedIds.get(id) ?? new Set<StructuralSource>();
    existing.add(source);
    claimedIds.set(id, existing);
  }
  console.log(`[structuralParaCache] "${source}" registered ${ids.length} ID(s). Cache total: ${claimedIds.size}`);
}

export function clearClaimedIds(): void {
  claimedIds.clear();
  activeSources.clear();
  console.log(`[structuralParaCache] Cache cleared.`);
}

// ── Read API (called by Run 6) ────────────────────────────────────────────

/** True if this para was claimed by ANY source that has already run. */
export function isParaClaimed(id: string): boolean {
  return claimedIds.has(id);
}

/** True if a specific source has run (regardless of whether it found anything). */
export function isSourceActive(source: StructuralSource): boolean {
  return activeSources.has(source);
}

export function getActiveSources(): ReadonlySet<StructuralSource> {
  return activeSources;
}

export function getCacheSize(): number {
  return claimedIds.size;
}